/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";

import { reportClientError } from "~/utils/report-client-error";

import {
  MODULE_RELOAD_BUILD_KEY,
  extractUpstreamFailure,
  is404,
  logBoundaryError,
  readLoadedBuildId,
  shouldReloadForModuleError,
} from "./error-utils";

const isProduction = import.meta.env.MODE === "production";

export const RootErrorBoundary = () => {
  const error = useRouteError();
  // Serialize + drop expected control flow (404s / bot probes / transient
  // network). A bare console.error(error) logged thrown Responses as
  // "[object Object]", hiding real faults; real 5xx/Error throws still log here.
  logBoundaryError(error, "RootErrorBoundary");

  // A stale/rotated JS chunk after a deploy (version skew) throws a module-load
  // error; a reload fetches fresh HTML + current chunk hashes so the shopper
  // never sees the offline screen. The loop guard is keyed on the BUILD rather
  // than the session — SFCC-11887 shipped it as once-per-session, and prod on
  // 2026-08-15 deployed four times in an afternoon, so a session spanning two
  // deploys spent its single reload on the first and got stranded on the second.
  // See `shouldReloadForModuleError` for why per-build is still loop-proof.
  const moduleReloadPending =
    typeof window !== "undefined" &&
    shouldReloadForModuleError(error, {
      buildId: readLoadedBuildId(),
      storage: (() => {
        try {
          return window.sessionStorage;
        } catch {
          return null;
        }
      })(),
    });

  // A stale chunk is the ONLY class a document reload fixes — new chunk hashes
  // need fresh HTML. Everything else that reaches this boundary is a real fault
  // and gets the offline screen. Notably an aborted fetch does NOT belong here:
  // the browser cancelling an in-flight request because the shopper navigated
  // away is not an error, and by the time a boundary renders, React has already
  // destroyed the subtree — so there is nothing this layer can do that is not a
  // downgrade (blank it, lie about it, or cancel their navigation). Those
  // rejections are kept off the router entirely by owning the fetch at the call
  // site instead (see `useFailSoftResource`) — SFCC-11869. `firedReload` guards
  // the render-body side effect below AND holds this true across the renders
  // during the reload, since `moduleReloadPending` re-reads the session key that
  // block writes.
  const firedReload = useRef(false);
  const willReload = moduleReloadPending || firedReload.current;
  const showsOfflineScreen = !willReload && !is404(error);

  // SFCC-11887: beacon each offline-screen render to the Workers client-error
  // endpoint so it lands in Cloudflare Logs. Until now the offline screen was
  // INVISIBLE server-side: a client-side crash follows a healthy 200 response,
  // so the boundary's `console.error` only reached the user's browser console
  // (never CF logs), and there is no RUM/Sentry. `reportClientError` is
  // SSR-safe (no-op on the server), consent-free, sendBeacon-based (survives
  // unload), and works on all three sites incl. WDH. This is the regression
  // detector for the offline screen (this bug or any future cause). Hook is
  // above the early return to respect the rules of hooks. De-duped by ERROR
  // IDENTITY — React keeps this instance when the router swaps `state.errors`, so
  // a boolean would silence every episode but the first.
  const beaconedFor = useRef<unknown>(null);
  useEffect(() => {
    if (!showsOfflineScreen || beaconedFor.current === error) return;
    beaconedFor.current = error;
    // `upstream*` are usually null client-side (a `Response` doesn't survive RR
    // error serialization — the authoritative capture is `handleError` on the
    // worker), but populate when a real `Response` DID reach the boundary
    // client-side, so those beacons are self-diagnosing too. Host+path only.
    const upstream = extractUpstreamFailure(error);
    const routeErrorStatus = isRouteErrorResponse(error) ? error.status : null;
    const upstreamStatus = upstream?.status ?? null;
    // `dedupeKey`: the statuses tell one offline screen from another, so they
    // split the dedupe; `upstreamUrl` is per occurrence and stays out (COM-448).
    reportClientError(
      "offline_screen",
      error,
      {
        routeErrorStatus,
        upstreamUrl: upstream?.url ?? null,
        upstreamStatus,
      },
      { dedupeKey: `${routeErrorStatus ?? "-"}/${upstreamStatus ?? "-"}` },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showsOfflineScreen, error]);

  // Reloading for a stale chunk: paint nothing while the fresh document loads.
  if (willReload) {
    // Render-body side effect, so the ref guard is what stops every subsequent
    // render firing another reload. `willReload` implies a client
    // (`moduleReloadPending` carries the `typeof window` check and the ref can
    // only be set here), so no SSR guard is needed.
    if (!firedReload.current) {
      firedReload.current = true;
      // Mark BEFORE reloading, against the build we are reloading AWAY FROM, so
      // a chunk still missing after the reload doesn't loop (same build id on
      // the next hit) while a LATER deploy still gets its own reload.
      try {
        window.sessionStorage.setItem(
          MODULE_RELOAD_BUILD_KEY,
          readLoadedBuildId(),
        );
      } catch {
        /* best-effort loop guard */
      }
      console.log("Recoverable error, reloading the page");

      window.location.reload();
    }
    return null;
  }

  const homeUrl = `/`;

  return (
    <div className="work-sans h-screen min-h-full bg-black text-base tracking-normal text-gray-600">
      {/* Error page UI */}
      {is404(error) && (
        <main className="relative isolate min-h-full">
          <img
            src="https://images.unsplash.com/photo-1545972154-9bb223aac798?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=3050&q=80&exp=8&con=-15&sat=-75"
            alt="Error page"
            className="absolute inset-0 -z-10 h-full w-full object-cover object-top"
          />
          <div className="mx-auto max-w-7xl px-6 py-32 text-center sm:py-40 lg:px-8">
            <p className="text-base font-semibold leading-8 text-white">404</p>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-5xl">
              Page not found
            </h1>
            <p className="mt-4 text-base text-white/70 sm:mt-6">
              Sorry, we couldn’t find the page you’re looking for.
            </p>
            <div className="mt-10 flex justify-center">
              <a
                href={homeUrl}
                className="text-sm font-semibold leading-7 text-white"
              >
                <span aria-hidden="true">&larr;</span> Back to home
              </a>
            </div>
          </div>
        </main>
      )}
      {!is404(error) && isRouteErrorResponse(error) && (
        <section className="flex h-full w-full items-center dark:bg-gray-900 dark:text-gray-100 sm:p-16">
          <div className="container mx-auto my-8 flex flex-col items-center justify-center space-y-8 px-5 py-10 text-center sm:max-w-md md:max-w-7xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              className="h-40 w-40 dark:text-gray-600"
            >
              <path
                fill="currentColor"
                d="M256,16C123.452,16,16,123.452,16,256S123.452,496,256,496,496,388.548,496,256,388.548,16,256,16ZM403.078,403.078a207.253,207.253,0,1,1,44.589-66.125A207.332,207.332,0,0,1,403.078,403.078Z"
              ></path>
              <rect
                width="176"
                height="32"
                x="168"
                y="320"
                fill="currentColor"
              ></rect>
              <polygon
                fill="currentColor"
                points="210.63 228.042 186.588 206.671 207.958 182.63 184.042 161.37 162.671 185.412 138.63 164.042 117.37 187.958 141.412 209.329 120.042 233.37 143.958 254.63 165.329 230.588 189.37 251.958 210.63 228.042"
              ></polygon>
              <polygon
                fill="currentColor"
                points="383.958 182.63 360.042 161.37 338.671 185.412 314.63 164.042 293.37 187.958 317.412 209.329 296.042 233.37 319.958 254.63 341.329 230.588 365.37 251.958 386.63 228.042 362.588 206.671 383.958 182.63"
              ></polygon>
            </svg>
            <p className="text-3xl">
              Looks like our services are currently offline
            </p>
            {!!error && !isProduction && (
              <div className="w-1/2 overflow-scroll border p-10">
                <div className="text-left">
                  <pre>
                    {JSON.stringify(
                      (error as any)?.error?.stack ||
                        (error as any).error?.message,
                    )}
                  </pre>
                </div>
              </div>
            )}

            <a
              rel="noopener noreferrer"
              href={homeUrl}
              className="rounded px-8 py-3 font-semibold dark:bg-violet-400 dark:text-gray-900"
            >
              Back to homepage
            </a>
          </div>
        </section>
      )}
      {!isRouteErrorResponse(error) && (
        <section className="flex h-full w-full items-center dark:bg-gray-900 dark:text-gray-100 sm:p-16">
          <div className="container mx-auto my-8 flex flex-col items-center justify-center space-y-8 px-5 py-10 text-center sm:max-w-md md:max-w-7xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              className="h-40 w-40 dark:text-gray-600"
            >
              <path
                fill="currentColor"
                d="M256,16C123.452,16,16,123.452,16,256S123.452,496,256,496,496,388.548,496,256,388.548,16,256,16ZM403.078,403.078a207.253,207.253,0,1,1,44.589-66.125A207.332,207.332,0,0,1,403.078,403.078Z"
              ></path>
              <rect
                width="176"
                height="32"
                x="168"
                y="320"
                fill="currentColor"
              ></rect>
              <polygon
                fill="currentColor"
                points="210.63 228.042 186.588 206.671 207.958 182.63 184.042 161.37 162.671 185.412 138.63 164.042 117.37 187.958 141.412 209.329 120.042 233.37 143.958 254.63 165.329 230.588 189.37 251.958 210.63 228.042"
              ></polygon>
              <polygon
                fill="currentColor"
                points="383.958 182.63 360.042 161.37 338.671 185.412 314.63 164.042 293.37 187.958 317.412 209.329 296.042 233.37 319.958 254.63 341.329 230.588 365.37 251.958 386.63 228.042 362.588 206.671 383.958 182.63"
              ></polygon>
            </svg>
            <p className="text-3xl">
              Looks like our services are currently offline
            </p>
            {!!error && error instanceof Error && !isProduction && (
              <div className="w-1/2 overflow-scroll border p-10">
                <div className="text-left">
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: addLinksToStackTrace(
                        error.stack || error.message,
                      ),
                    }}
                  />
                </div>
              </div>
            )}

            <a
              rel="noopener noreferrer"
              href={homeUrl}
              className="rounded px-8 py-3 font-semibold dark:bg-violet-400 dark:text-gray-900"
            >
              Back to homepage
            </a>
          </div>
        </section>
      )}
    </div>
  );
};

function addLinksToStackTrace(stackTrace: string) {
  return String(stackTrace)?.replace(
    /^\s*at\s?.*?[(\s]((\/|\w).+)\)\n/gim,
    (all, m1) =>
      all.replace(
        m1,
        `<a href="vscode://file${m1}" class="hover:underline">${m1}</a>`,
      ),
  );
}
